import { Box, Chip } from '@mui/material';
import type { KeyMetricSpec, UsageCostBreakdown } from '../../types';

interface Props {
  metrics: KeyMetricSpec[];
  breakdown: UsageCostBreakdown | null;
  params: Record<string, unknown>;
}

function readNumerator(
  breakdown: UsageCostBreakdown | null,
  expr: string
): number {
  if (!breakdown) return 0;
  if (expr === 'breakdown.total_monthly') return breakdown.total_monthly;
  if (expr === 'breakdown.total_yearly') return breakdown.total_yearly;
  const m = expr.match(/^workflow:([a-zA-Z_][a-zA-Z0-9_]*)\.workflow_cost$/);
  if (m) {
    const cfg = breakdown.configs.find((c) => c.config_name === m[1]);
    return cfg ? cfg.config_cost : 0;
  }
  return 0;
}

function evalDenominator(
  expr: string,
  params: Record<string, unknown>
): number {
  const parts = expr.split('*').map((s) => s.trim());
  let product = 1;
  for (const p of parts) {
    const v = params[p];
    if (typeof v === 'number') product *= v;
    else if (typeof v === 'string') {
      const n = Number(v);
      if (Number.isNaN(n)) return 0;
      product *= n;
    } else return 0;
  }
  return product;
}

function formatNumber(value: number, fmt: string): string {
  const m = fmt.match(/^\.(\d+)f$/);
  const decimals = m ? Number(m[1]) : 2;
  return value.toFixed(decimals);
}

export function KeyMetricsBar({ metrics, breakdown, params }: Props) {
  if (!breakdown || metrics.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      {metrics.map((m, i) => {
        const num = readNumerator(breakdown, m.numerator);
        const denom = evalDenominator(m.denominator, params);
        if (m.hide_when_zero_denominator && denom === 0) return null;
        const value = denom === 0 ? 0 : num / denom;
        const formatted = formatNumber(value, m.format);
        // Replace the "%.Xf" marker in the label with the formatted value.
        const label = m.label.replace(/%\.\d+f/, formatted);
        return (
          <Chip
            key={`${m.label}-${i}`}
            label={label}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{ fontWeight: 600, borderWidth: 2 }}
          />
        );
      })}
    </Box>
  );
}
